import { CHART_MAX_SERIES } from '../constants/charts';
import formatDatasourceCell from './formatDatasourceCell';
import readDatasourceNumber from './readDatasourceNumber';

import type { IPieChartData, IPieChartSlice } from '../interfaces/IAiChatChartData';
import type { IAiChatDatasourceColumn, IPieChartShown } from '../interfaces/IAiChatTools';

/**
 * The slices of a pie chart from the rows of its datasource, by the mapping the tool resolved.
 *
 * One slice per row, in the order the rows came out. A row whose value is missing, not a number,
 * or not positive has no slice. Past the cap, the smallest slices are folded into one named
 * `otherLabel` at the end, so the chart never hands out a colour it does not have.
 */
const buildPieChartData = (
  columns: IAiChatDatasourceColumn[],
  rows: unknown[][],
  shown: IPieChartShown,
  otherLabel: string,
): IPieChartData => {
  const labelIndex = columns.findIndex((column) => column.name === shown.labelColumn.name);
  const valueIndex = columns.findIndex((column) => column.name === shown.valueColumn.name);

  const slices = rows.reduce<IPieChartSlice[]>((result, row) => {
    const value = readDatasourceNumber(row[valueIndex]);

    if (value !== null && value > 0) {
      result.push({ label: formatDatasourceCell(row[labelIndex], shown.labelColumn.pgType), value });
    }

    return result;
  }, []);

  if (slices.length <= CHART_MAX_SERIES) {
    return { slices, foldedSlices: 0 };
  }

  // The largest keep their place; the rest become one slice at the end.
  const kept = new Set(
    [...slices].sort((a, b) => b.value - a.value).slice(0, CHART_MAX_SERIES - 1),
  );
  const folded = slices.filter((slice) => !kept.has(slice));
  const other: IPieChartSlice = {
    label: otherLabel,
    value: folded.reduce((sum, slice) => sum + slice.value, 0),
  };

  return {
    slices: [...slices.filter((slice) => kept.has(slice)), other],
    foldedSlices: folded.length,
  };
};

export default buildPieChartData;
